import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

const COMPACT_FORMATTER = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
})

// Below 100k: localized with commas. At/above: lowercase abbreviation
// (e.g. 400k, 1.5m) per the Figma tooltip spec.
export function formatTooltipNumber(value: number): string {
  if (Math.abs(value) < 100_000) return value.toLocaleString()
  return COMPACT_FORMATTER.format(value).toLowerCase()
}

// Chart axis ticks: compact for anything 1,000+ (e.g. 1k, 12.5k, 1m).
export function formatAxisNumber(value: number): string {
  if (Math.abs(value) < 1_000) return value.toLocaleString()
  return COMPACT_FORMATTER.format(value).toLowerCase()
}

// Card heading counts: spell out thousand/million/billion/trillion. 2 sig figs.
// Examples: 42 → "42", 1,100 → "1.1 thousand", 25,000 → "25 thousand",
// 2,000,000 → "2 million", 1,234,567 → "1.2 million", 3,400,000,000 → "3.4 billion".
export function formatScaleCount(value: number): string {
  const abs = Math.abs(value)
  if (abs < 1_000) return value.toLocaleString()
  const sign = value < 0 ? '-' : ''
  const tiers: { divisor: number; label: string }[] = [
    { divisor: 1_000_000_000_000, label: 'trillion' },
    { divisor: 1_000_000_000, label: 'billion' },
    { divisor: 1_000_000, label: 'million' },
    { divisor: 1_000, label: 'thousand' },
  ]
  for (const { divisor, label } of tiers) {
    if (abs >= divisor) {
      const scaled = abs / divisor
      // 2 sig figs: drop the decimal once we hit two-digit values (e.g. 12, not 12.3).
      const formatted = scaled >= 10 ? Math.round(scaled).toString() : scaled.toFixed(1).replace(/\.0$/, '')
      return `${sign}${formatted} ${label}`
    }
  }
  return value.toLocaleString()
}
